"use client";

import Link from "next/link";
import { FormEvent, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@iconify/react";
import { setAuthToken } from "@/lib/client-auth";
import { formatApiError } from "@/lib/client-api";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);
  const [nextParam, setNextParam] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "success") {
      setSuccess("Password reset successfully. You can log in with your new password.");
    }
    const reason = params.get("reason");
    if (reason) {
      setError(decodeURIComponent(reason));
    }
    setCheckoutPlan(params.get("checkoutPlan"));
    setNextParam(params.get("next"));
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(formatApiError(data, "Failed to login", response.status));
        return;
      }
      setAuthToken(data.token);

      if (checkoutPlan === "starter" || checkoutPlan === "pro") {
        const checkoutResponse = await fetch("/api/billing/checkout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.token}`,
          },
          body: JSON.stringify({ plan: checkoutPlan }),
        });
        const checkoutData = await checkoutResponse.json();
        if (checkoutResponse.ok && checkoutData.checkoutUrl) {
          window.location.href = checkoutData.checkoutUrl as string;
          return;
        }
      }

      if (nextParam && nextParam.startsWith("/")) {
        router.push(nextParam);
        return;
      }
      router.push("/dashboard");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-[#FAFAFA] min-h-screen flex flex-col items-center justify-center px-4 selection:bg-[#5B8CFF]/20">
      {/* Ambient Background Glow */}
      <div className="pointer-events-none fixed inset-0 flex justify-center overflow-hidden z-0">
        <div className="h-[40rem] w-[80rem] flex-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#5B8CFF]/10 via-[#FAFAFA] to-[#FAFAFA] opacity-50"></div>
      </div>

      {/* Back to home */}
      <div className="relative z-10 w-full max-w-sm mb-6">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <Icon icon="solar:arrow-left-linear" className="text-base" />
          Back to home
        </Link>
      </div>

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-xl shadow-sm p-8">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#5B8CFF] to-[#22C55E] text-white shadow-[0_0_15px_rgba(91,140,255,0.3)]">
            <Icon icon="solar:ghost-smile-linear" className="text-xl" />
          </div>
          <span className="text-lg font-semibold tracking-tight text-gray-900">
            myaiproxy.app
          </span>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-1">
          Sign in
        </h1>
        <p className="text-sm text-gray-500 mb-7">
          Access your proxy dashboard.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <input
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-[#5B8CFF] focus:ring-2 focus:ring-[#5B8CFF]/10"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="space-y-1">
            <input
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-[#5B8CFF] focus:ring-2 focus:ring-[#5B8CFF]/10"
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="flex justify-end pt-1">
              <Link
                href="/forgot-password"
                className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {success && (
            <p className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-100 px-3 py-2 text-xs text-green-700">
              <Icon icon="solar:check-circle-linear" className="text-base shrink-0" />
              {success}
            </p>
          )}
          {error && (
            <p className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-600">
              <Icon icon="solar:danger-circle-linear" className="text-base shrink-0" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-3 text-sm font-medium text-white transition-all hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {loading ? (
              <>
                <Icon icon="solar:spinner-linear" className="mr-2 text-base animate-spin" />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Don&apos;t have an account?{" "}
          <Link href="/register" className="font-medium text-gray-900 hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
